import { AlertTriangle, ShieldOff } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  RISK_WARNING_DAYS_MIN,
  SUSPENSION_DAYS_MAX,
  SUSPENSION_DAYS_MIN,
  SUSPENSION_GRACE_HOURS_MAX,
  SUSPENSION_GRACE_HOURS_MIN,
  formatBRDateTime,
  type SuspensionConfigDto,
  type UpdateSuspensionConfigDto,
} from '@control-center/shared';

import { PillButton } from '../../components/clients/PillButton';
import { Field, Input } from '../../components/clients/form-primitives';
import { PageHelp } from '../../components/common/PageHelp';
import { useAuth } from '../../hooks/use-auth';
import { useSuspensionConfig, useUpdateSuspensionConfig } from '../../hooks/use-suspension-policy';
import { extractErrorMessage } from '../../lib/errors';

interface FormState {
  suspensionEnabled: boolean;
  daysAfterDue: string;
  graceHours: string;
  riskWarningDays: string;
}

function toFormState(config: SuspensionConfigDto): FormState {
  return {
    suspensionEnabled: config.suspension_enabled,
    daysAfterDue: String(config.suspension_days_after_due),
    graceHours: String(config.suspension_grace_hours),
    riskWarningDays: String(config.risk_warning_days_before),
  };
}

interface FieldErrors {
  daysAfterDue?: string;
  graceHours?: string;
  riskWarningDays?: string;
}

/** Validação inline espelhando o backend (AC8). Backend é a verdade — isto é UX. */
function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  const days = Number(form.daysAfterDue);
  const grace = Number(form.graceHours);
  const risk = Number(form.riskWarningDays);

  if (!Number.isInteger(days) || days < SUSPENSION_DAYS_MIN || days > SUSPENSION_DAYS_MAX) {
    errors.daysAfterDue = `Entre ${SUSPENSION_DAYS_MIN} e ${SUSPENSION_DAYS_MAX} dias.`;
  }
  if (
    !Number.isInteger(grace) ||
    grace < SUSPENSION_GRACE_HOURS_MIN ||
    grace > SUSPENSION_GRACE_HOURS_MAX
  ) {
    errors.graceHours = `Entre ${SUSPENSION_GRACE_HOURS_MIN} e ${SUSPENSION_GRACE_HOURS_MAX} horas.`;
  }
  if (!Number.isInteger(risk) || risk < RISK_WARNING_DAYS_MIN) {
    errors.riskWarningDays = `Mínimo ${RISK_WARNING_DAYS_MIN} dia.`;
  } else if (Number.isInteger(days) && risk >= days) {
    errors.riskWarningDays =
      'Aviso deve ocorrer antes da suspensão (menor que dias após vencimento).';
  }
  return errors;
}

export function AdminSuspensionPolicyPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const query = useSuspensionConfig(isAdmin);
  const updateMutation = useUpdateSuspensionConfig();

  const [form, setForm] = useState<FormState | null>(null);

  // Hidrata o form quando a config chega. Reaplica em refetch (version nova).
  useEffect(() => {
    if (query.data) setForm(toFormState(query.data));
  }, [query.data]);

  const errors = useMemo(() => (form ? validate(form) : {}), [form]);
  const hasErrors = Object.keys(errors).length > 0;

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div className="flex items-center gap-3 rounded-[12px] border border-line bg-panel px-5 py-4 text-sm text-muted">
          <ShieldOff size={18} className="text-muted-3" />
          Apenas administradores podem configurar a política de suspensão.
        </div>
      </div>
    );
  }

  function patch(part: Partial<FormState>) {
    setForm((prev) => (prev ? { ...prev, ...part } : prev));
  }

  function handleToggleEnabled() {
    if (!form) return;
    // Aviso de confirmação ao LIGAR (AC11) — desligar é seguro, não confirma.
    if (!form.suspensionEnabled) {
      const days = Number(form.daysAfterDue);
      const confirmed = window.confirm(
        `Ativar suspensão automática irá bloquear o acesso de clientes inadimplentes há ${
          Number.isInteger(days) ? days : '?'
        } dias. Confirma?`,
      );
      if (!confirmed) return;
    }
    patch({ suspensionEnabled: !form.suspensionEnabled });
  }

  async function handleSave() {
    if (!form || !query.data || hasErrors) return;
    const dto: UpdateSuspensionConfigDto = {
      suspension_enabled: form.suspensionEnabled,
      suspension_days_after_due: Number(form.daysAfterDue),
      suspension_grace_hours: Number(form.graceHours),
      risk_warning_days_before: Number(form.riskWarningDays),
      version: query.data.version,
    };
    try {
      await updateMutation.mutateAsync(dto);
      toast.success('Política de suspensão salva.');
    } catch (err) {
      toast.error('Não conseguimos salvar a política.', {
        description: extractErrorMessage(err, 'Tente novamente.'),
      });
      // Em conflito de versão (409), recarrega a config do servidor pra o usuário
      // ver o estado atual antes de tentar de novo (optimistic lock).
      await query.refetch();
    }
  }

  const config = query.data;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-6">
      <div className="flex items-start gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Política de suspensão</h1>
          <p className="mt-1 text-sm text-muted">
            Define quando e como a suspensão automática por inadimplência opera. Global, com
            override por SaaS e opt-out por cliente.
          </p>
        </div>
        <PageHelp id="admin-suspension-policy" />
      </div>

      {query.isLoading && <div className="text-sm text-muted">Carregando configuração…</div>}

      {query.isError && (
        <div className="rounded-[12px] border border-bad/40 bg-bad/10 px-5 py-4 text-sm text-red-200">
          {extractErrorMessage(query.error, 'Não conseguimos carregar a configuração.')}
        </div>
      )}

      {form && config && (
        <div className="flex flex-col gap-6">
          {/* Toggle master ----------------------------------------------------- */}
          <div className="rounded-[12px] border border-line bg-panel p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-semibold text-ink">Suspensão automática</div>
                <p className="mt-1 max-w-md text-[12px] leading-relaxed text-muted-2">
                  Quando ligada, clientes inadimplentes elegíveis têm o acesso bloqueado
                  automaticamente após o prazo configurado. Desligada (padrão), nada é suspenso sem
                  ação manual.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={form.suspensionEnabled}
                onClick={handleToggleEnabled}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  form.suspensionEnabled ? 'bg-ink' : 'bg-line-2'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-panel transition-transform ${
                    form.suspensionEnabled ? 'translate-x-[22px]' : 'translate-x-[2px]'
                  }`}
                />
              </button>
            </div>

            {form.suspensionEnabled && (
              <div className="mt-4 flex items-center gap-2 rounded-[10px] border border-warn/40 bg-warn/10 px-3 py-2 text-[12px] text-warn">
                <AlertTriangle size={14} className="shrink-0" />
                Suspensão automática ativa: liga o bloqueio por inadimplência nos SaaS habilitados.
              </div>
            )}
          </div>

          {/* Parâmetros -------------------------------------------------------- */}
          <div className="grid gap-5 rounded-[12px] border border-line bg-panel p-5 sm:grid-cols-3">
            <Field
              label="Dias após vencimento"
              hint="Suspende após N dias de inadimplência (D+N)."
              error={errors.daysAfterDue}
            >
              {({ inputId, describedBy }) => (
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  type="number"
                  inputMode="numeric"
                  min={SUSPENSION_DAYS_MIN}
                  max={SUSPENSION_DAYS_MAX}
                  value={form.daysAfterDue}
                  hasError={Boolean(errors.daysAfterDue)}
                  onChange={(e) => patch({ daysAfterDue: e.target.value })}
                  tabular
                />
              )}
            </Field>

            <Field
              label="Carência (horas)"
              hint="Horas extras antes de efetivar a suspensão."
              error={errors.graceHours}
            >
              {({ inputId, describedBy }) => (
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  type="number"
                  inputMode="numeric"
                  min={SUSPENSION_GRACE_HOURS_MIN}
                  max={SUSPENSION_GRACE_HOURS_MAX}
                  value={form.graceHours}
                  hasError={Boolean(errors.graceHours)}
                  onChange={(e) => patch({ graceHours: e.target.value })}
                  tabular
                />
              )}
            </Field>

            <Field
              label="Aviso de risco (dias antes)"
              hint="Quantos dias antes da suspensão avisar o cliente."
              error={errors.riskWarningDays}
            >
              {({ inputId, describedBy }) => (
                <Input
                  id={inputId}
                  aria-describedby={describedBy}
                  type="number"
                  inputMode="numeric"
                  min={RISK_WARNING_DAYS_MIN}
                  value={form.riskWarningDays}
                  hasError={Boolean(errors.riskWarningDays)}
                  onChange={(e) => patch({ riskWarningDays: e.target.value })}
                  tabular
                />
              )}
            </Field>
          </div>

          {/* Footer: meta + ações ---------------------------------------------- */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-[12px] text-muted-2">
              {config.updated_by_user_id
                ? `Última alteração por: ${config.updated_by_user_id} em ${formatBRDateTime(
                    new Date(config.updated_at),
                  )}`
                : 'Configuração nunca alterada (valores padrão).'}
            </p>
            <PillButton
              solid
              onClick={() => void handleSave()}
              disabled={hasErrors || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Salvando…' : 'Salvar política'}
            </PillButton>
          </div>
        </div>
      )}
    </div>
  );
}
